import { useEffect, useRef } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Hero from '@/pages/home/components/Hero';
import TrustSection from '@/pages/home/components/TrustSection';
import CategoryGrid from '@/pages/home/components/CategoryGrid';
import GuideSection from '@/pages/home/components/GuideSection';
import ComparisonSection from '@/pages/home/components/ComparisonSection';
import TestimonialSection from '@/pages/home/components/TestimonialSection';
import ArticleGrid from '@/pages/home/components/ArticleGrid';
import FAQSection from '@/pages/home/components/FAQSection';
import CTASection from '@/pages/home/components/CTASection';

export default function Home() {
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    const elements = document.querySelectorAll('.section-observer');
    elements.forEach((el) => observerRef.current?.observe(el));

    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  return (
    <main>
      <Navbar />
      <Hero />

      {/* Organization structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'OOPBuy Spreadsheet',
            url: 'https://oopbuyspread.casa',
            description: 'OOPBuy Spreadsheet – Dein vertrauenswürdiger Guide für OOPBuy in Deutschland.',
            logo: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/5133451b25e408e4a25e4df97e05add8.jpeg',
          }),
        }}
      />

      {/* WebSite structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: 'OOPBuy Spreadsheet',
            url: 'https://oopbuyspread.casa',
            description: 'Der ultimative OOPBuy Guide für Deutschland mit den besten Produkten, Verkäufern und Tipps.',
            inLanguage: 'de',
            potentialAction: {
              '@type': 'SearchAction',
              target: 'https://oopbuyspread.casa/search?q={search_term_string}',
              'query-input': 'required name=search_term_string',
            },
          }),
        }}
      />
      <div className="section-observer">
        <TrustSection />
      </div>
      <div className="section-observer">
        <CategoryGrid />
      </div>
      <GuideSection />
      <div className="section-observer">
        <ComparisonSection />
      </div>
      <div className="section-observer">
        <TestimonialSection />
      </div>
      <div className="section-observer">
        <ArticleGrid />
      </div>
      <div className="section-observer">
        <FAQSection />
      </div>
      <div className="section-observer">
        <CTASection />
      </div>
      <Footer />

      {/* Homepage structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'OOPBuy Spreadsheet Deutschland 2026',
            description: 'Der ultimative OOPBuy Guide für Deutschland mit den besten Produkten, Verkäufern und Tipps.',
            url: 'https://oopbuyspread.casa/',
            isPartOf: {
              '@type': 'WebSite',
              name: 'OOPBuy Spreadsheet',
              url: 'https://oopbuyspread.casa',
            },
            about: {
              '@type': 'Thing',
              name: 'OOPBuy Spreadsheet',
              description: 'Ein kuratiertes Tool für OOPBuy-Nutzer mit den besten Produkten und Verkäufern.',
            },
          }),
        }}
      />
    </main>
  );
}